import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { generateOrganizationTemplateText } from 'KnmErknmPackage/MainModule/common/generateOrganizationTemplateText';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IKnmErknmFormResponsibleEntitie } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResponsibleEntitie';
import { IKnmOrganizationOptions } from 'CorePackage/CatalogsModule/interfaces/IKnmOrganizationOptions';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ResponsibilityTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/ResponsibilityTypeCodesEnum';
import { IKnmErknmFormStructuresNPA } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormStructuresNPA';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';

import { ErknmResultItemSubjectItem } from './ErknmResultItemSubjectItem';
import styles from './ErknmResultDecisionResponsibleEntitiesItem.module.scss';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { IKnmErknmFormOrganizationAdd } from '../../MainModule/interfaces/IKnmErknmFormOrganizationAdd';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IErknmResultDecisionResponsibleEntitiesItemProps {
  acts: IKnmErknmFormAct[] | undefined;
  addField: (fieldName: string) => () => void;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  fieldValue: IKnmErknmFormResponsibleEntitie;
  fields: {
    update: (index: number, value: IKnmErknmFormResponsibleEntitie) => void;
    value: IKnmErknmFormResponsibleEntitie[];
  };
  formRef: IKnmFormRef | undefined;
  id: string;
  isWatchOnly: boolean;
  lateFieldName: string;
  name: string;
  organizations?: IKnmErknmFormSubject[] | undefined;
  responsibleEntitiesBlockId: string;
  responsibleEntitiesItemIndex: number;
  responsibleEntitiesItemName: string;
}

interface IErknmResultDecisionResponsibleEntitiesItemStoreProps {}

export const ErknmResultDecisionResponsibleEntitiesItemWithoutStore = ({
  acts,
  addField,
  eventsErknm,
  fieldValue,
  fields,
  formRef,
  id,
  isWatchOnly,
  lateFieldName,
  name,
  organizations,
  responsibleEntitiesBlockId,
  responsibleEntitiesItemIndex,
  responsibleEntitiesItemName,
}: IErknmResultDecisionResponsibleEntitiesItemProps & IErknmResultDecisionResponsibleEntitiesItemStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const addPunishmentUins = (fieldName: string) => () => !isWatchOnly && push && push(fieldName, '');
  const makeRemoveField = (fieldName: string, index: number) => () =>
    !isWatchOnly && remove && remove(`${name}.${fieldName}`, index);
  const currentResponsibilityCode = fieldValue.responsibilityType?.code;
  const isOrganizationSelected = !!fieldValue.organization;
  const organizationFieldName = `${responsibleEntitiesItemName}.organization` as keyof IKnmForm;
  const [isRemovingOtherOrganization, setIsRemovingOtherOrganization] = useState(false);
  const isShowOtherOrganization = isOrganizationSelected && !isRemovingOtherOrganization;

  // Костыль для формы Ответственного лица.
  // Без глобальной переделки (расшить селектор новое/выбираемое на два отдельных значения) без него обойтись не выйдет
  useEffect(() => {
    if (!isRemovingOtherOrganization) {
      return;
    }

    // Вложенные organization.value.* уже размонтированы — теперь можно безопасно
    // записать строковую ошибку на organization (иначе setIn в final-form портит её в объект).
    formRef?.current.change(organizationFieldName, undefined);
    formRef?.current.focus(organizationFieldName);
    formRef?.current.blur(organizationFieldName);
    setIsRemovingOtherOrganization(false);
  }, [formRef, isRemovingOtherOrganization, organizationFieldName]);

  /**
   * Список контролируемых лиц, для выбора в качестве привлеченных к ответственности
   */
  const responsibleEntitiesOptions = useMemo(() => {
    const options: IKnmOrganizationOptions[] = [];

    const isAction = !!eventsErknm?.find(i => i.interactionCategory?.id === 3 || i.interactionCategory?.id === 2);

    if (isAction) {
      acts?.forEach(item => {
        if (!options.find(i => i.value?.guid === item.organization?.guid)) {
          const label = generateOrganizationTemplateText(
            item.organization?.inn || undefined,
            item.organization?.ogrn || undefined,
            item.organization?.organizationName || undefined,
            item.organization?.rafpNza || undefined,
          );

          options.push({
            id: item.guid,
            value: item.organization,
            label,
          } as IKnmOrganizationOptions);
        }
      });
    } else {
      organizations?.forEach(subject => {
        const label = generateOrganizationTemplateText(
          subject.inn || undefined,
          subject.ogrn || undefined,
          subject.organizationName || undefined,
          subject.rafpNza || undefined,
        );

        options.push({
          id: subject.guid,
          value: {
            ...subject,
            isNew: false,
          },
          label,
        } as IKnmOrganizationOptions);
      });
    }

    return options;
  }, [acts, eventsErknm, organizations]);

  const onChangePunishmentAmount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue) {
        const numericValue = inputValue.replace(/[^\d]/g, '');

        if (numericValue) {
          try {
            const bigValue = BigInt(numericValue);
            const maxSafe = BigInt(Number.MAX_SAFE_INTEGER);

            if (bigValue > maxSafe) {
              e.target.value = Number.MAX_SAFE_INTEGER.toString();
              formRef?.current.change(
                `${responsibleEntitiesItemName}.punishmentAmount` as keyof IKnmForm,
                Number.MAX_SAFE_INTEGER.toString(),
              );

              return;
            }
          } catch (error) {
            console.error('Ошибка преобразования числа');
            formRef?.current.change(`${responsibleEntitiesItemName}.punishmentAmount` as keyof IKnmForm);
          }
        }
      }
    },
    [formRef, responsibleEntitiesItemName],
  );

  return (
    <Row isListItem margin="medium">
      {!isOrganizationSelected && (
        <Row>
          <SelectInputField
            name={organizationFieldName}
            id={`${id}.organization`}
            placeholder={getTitleFieldWithRedStar({
              label: 'Выберите контролируемое лицо',
              field: `${organizationFieldName}`,
              isKnm: true,
            })}
            options={responsibleEntitiesOptions}
            disabled={isWatchOnly}
            className={styles.SelectInputDecisionsResponsibleEntities}
            size="medium"
          />
          <Button
            className={`shared-btn-add ${styles.otherButton}`}
            text="Добавить иное"
            type="button"
            variant="link"
            color="primary"
            disabled={isWatchOnly}
            onClick={() =>
              fields.update(responsibleEntitiesItemIndex, {
                ...fieldValue,
                organization: {
                  id: fieldValue.organization?.id || '',
                  label: fieldValue.organization?.label || '',
                  value: {
                    isAutocomplete: false,
                    isFiz: false,
                    isForeignSubjectAgencyNotRegistredInRf: false,
                  } as IKnmErknmFormOrganizationAdd,
                  isOther: true,
                },
              })
            }
          />
          <KnmLateFieldsRemark
            lateFieldPath={`${lateFieldName}/organization`}
            fieldId={`${responsibleEntitiesItemName}.organization`}
          />
        </Row>
      )}
      <Row>
        {!isShowOtherOrganization ? null : (
          <ErknmResultItemSubjectItem
            id={`${id}.organization.value`}
            name={`${responsibleEntitiesItemName}.organization.value`}
            isWatchOnly={isWatchOnly}
            watchOnlyExtended={isWatchOnly}
            disableFieldByCompleteStatuses={isWatchOnly}
            disableFieldByInProcessStatuses={isWatchOnly}
            isCollapsing={false}
            okveds={fieldValue.organization?.value?.okveds}
            inn={fieldValue.organization?.value?.inn}
            ogrn={fieldValue.organization?.value?.ogrn}
            firstOrganizationName={fieldValue.organization?.value?.firstOrganizationName}
            checkSubjectResult={fieldValue.organization?.value?.checkSubjectResult || null}
            isAutocomplete={fieldValue.organization?.value?.isAutocomplete}
            isFiz={fieldValue.organization?.value?.isFiz}
            isMultiFields={true}
            mspCode={fieldValue.organization?.value?.mspCode}
            mspRegistryDate={fieldValue.organization?.value?.mspRegistryDate}
            isNew={fieldValue.organization?.isOther || false}
            isNewOrganizationTemplate={fieldValue.organization?.isOther || false}
            subjectType={fieldValue.organization?.value?.subjectType}
            kpp={fieldValue.organization?.value?.kpp}
            smevRequestStatus={fieldValue.organization?.value?.smevRequestStatus}
            rafpNza={fieldValue.organization?.value?.rafpNza}
            isSonko={fieldValue.organization?.value?.isSonko}
            snils={fieldValue.organization?.value?.snils}
            ervkId={fieldValue.organization?.value?.ervkId}
            isPresentInItRegistry={fieldValue.organization?.value?.isPresentInItRegistry}
            itAccreditationResponse={fieldValue.organization?.value?.itAccreditationResponse}
            smevItAccreditationRequestStatus={fieldValue.organization?.value?.smevItAccreditationRequestStatus}
            isOtherOrganization={fieldValue.organization?.isOther}
            removeOtherOrganization={() => {
              setIsRemovingOtherOrganization(true);
            }}
          />
        )}
      </Row>

      <Row
        title={getTitleFieldWithRedStar({
          label: 'Сведения о виде назначенного наказания',
          field: `${responsibleEntitiesItemName}.responsibilityType`,
          isKnm: true,
        })}
        notWrap
      >
        <SelectInputFieldAutoOptions
          className={styles.AssignedPunishmentNoteSelectInput}
          id={`${id}.responsibilityType`}
          name={`${responsibleEntitiesItemName}.responsibilityType`}
          placeholder="Выберите вид наказания"
          size="medium"
          optionName={CommonCatalogsNamesEnum.RESPONSIBILITY_TYPES}
          disabled={isWatchOnly}
          isClearable={false}
        />
        <KnmLateFieldsRemark
          lateFieldPath={`${lateFieldName}/responsibilityType`}
          fieldId={`${id}.responsibilityType`}
          isLeftPosition={true}
        />
      </Row>

      {(currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_IV ||
        currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_V) && (
        <Row title="Комментарий" notSpace>
          <FieldBlock id={`${id}.punishmentNote`}>
            <TextInputField name={`${responsibleEntitiesItemName}.punishmentNote`} disabled={isWatchOnly} />
          </FieldBlock>
          <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/punishmentNote`} fieldId={`${id}.punishmentNote`} />
        </Row>
      )}

      {currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_I && (
        <div className={styles.Punishment}>
          <div className={styles.PunishmentAmount}>
            <Row title="Сведения о размере наказания" notSpace notWrap>
              <FieldBlock size="half" margin="right" id={`${id}.punishmentAmount`}>
                <TextInputField
                  name={`${responsibleEntitiesItemName}.punishmentAmount`}
                  placeholder="Сумма"
                  disabled={isWatchOnly}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={onChangePunishmentAmount}
                  parse={(value: string | undefined) => {
                    if (value === undefined) return value;

                    const cleaned = value.replace(/\D/g, '');

                    return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                  }}
                />
              </FieldBlock>
              <KnmLateFieldsRemark
                lateFieldPath={`${lateFieldName}/punishmentAmount`}
                fieldId={`${id}.punishmentAmount`}
              />
              <FieldBlock size="half" id={`${id}.punishmentAmountMeasure`}>
                <TextInputField
                  name={`${responsibleEntitiesItemName}.punishmentAmountMeasure`}
                  placeholder="Ед. измерения"
                  disabled={isWatchOnly}
                />
              </FieldBlock>
              <KnmLateFieldsRemark
                lateFieldPath={`${lateFieldName}/punishmentAmountMeasure`}
                fieldId={`${id}.punishmentAmountMeasure`}
              />
            </Row>
          </div>
          <div className={styles.PunishmentUins}>
            <FieldBlock
              label={<>УИН</>}
              id={responsibleEntitiesBlockId}
              button={
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={isWatchOnly}
                  onClick={addPunishmentUins(`${responsibleEntitiesItemName}.punishmentUins`)}
                />
              }
            >
              <FieldArray name={`${responsibleEntitiesItemName}.punishmentUins`}>
                {({ fields: uins }: FieldArrayRenderProps<string, HTMLElement>) => {
                  if (!uins.length) {
                    return null;
                  }

                  return uins.map(uinName => {
                    return (
                      <Row notSpace notWrap key={uinName}>
                        <FieldBlock id={uinName}>
                          <TextInputField type="number" name={uinName} placeholder="УИН" disabled={isWatchOnly} />
                        </FieldBlock>
                        <KnmLateFieldsRemark
                          lateFieldPath={`${lateFieldName}/punishmentUins`}
                          fieldId={uinName}
                          isLeftPosition={true}
                        />
                      </Row>
                    );
                  });
                }}
              </FieldArray>
            </FieldBlock>
          </div>
        </div>
      )}

      {(currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_II ||
        currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_III) && (
        <Row title="Срок">
          <TextInputField
            className={styles.PunishmentTermTextInput}
            id={`${id}.punishmentTerm`}
            name={`${responsibleEntitiesItemName}.punishmentTerm`}
            placeholder="Введите срок"
            disabled={isWatchOnly}
          />
          <KnmLateFieldsRemark
            lateFieldPath={`${lateFieldName}/punishmentTerm`}
            fieldId={`${id}.punishmentTerm`}
            isLeftPosition={true}
          />
        </Row>
      )}

      <FieldBlock
        label={
          <>
            {getTitleFieldWithRedStar({
              label:
                'Структурные единицы нормативных правовых актов, содержащих информацию \n о мерах ответственности контролируемых лиц',
              field: `${responsibleEntitiesItemName}.structuresNPA`,
              isKnm: true,
            })}
            <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/structuresNPA`} />
          </>
        }
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={isWatchOnly}
            onClick={addField(
              `${responsibleEntitiesItemName.slice(responsibleEntitiesItemName.indexOf('.') + 1)}.structuresNPA`,
            )}
          />
        }
        id={`${id}.structuresNPA`}
      >
        <FieldArray name={`${responsibleEntitiesItemName}.structuresNPA`}>
          {({ fields: structuresNpaFields }: FieldArrayRenderProps<IKnmErknmFormStructuresNPA, HTMLElement>) => {
            if (!structuresNpaFields.length) {
              return null;
            }

            return (
              <Row>
                {structuresNpaFields.map((structuresFieldName, structuresIndex) => {
                  const structuresNpaFieldValue = structuresNpaFields.value?.[structuresIndex];
                  const structuresNpaId = `${id}.structuresNPA[${structuresNpaFieldValue?.listId}]`;

                  return (
                    <Row key={structuresFieldName} isListItem>
                      <TextareaField
                        className="shared-deleting-row"
                        id={`${structuresNpaId}.value`}
                        name={`${structuresFieldName}.value`}
                        placeholder="Введите значение"
                        resize="none"
                        disabled={isWatchOnly}
                      />
                      <CloseButton
                        className="shared-deleting-row-btn"
                        onClick={makeRemoveField(
                          `${responsibleEntitiesItemName.slice(
                            responsibleEntitiesItemName.indexOf('.') + 1,
                          )}.structuresNPA`,
                          structuresIndex,
                        )}
                      />
                      <KnmLateFieldsRemark
                        lateFieldPath={`${lateFieldName}/structuresNPA/${structuresNpaFieldValue?.listId}/value`}
                        fieldId={`${structuresFieldName}.value`}
                        isLeftPosition={true}
                      />
                    </Row>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
    </Row>
  );
};

const mapStateToProps = (): IErknmResultDecisionResponsibleEntitiesItemStoreProps => ({});

export const ErknmResultDecisionResponsibleEntitiesItem = connect(mapStateToProps)(
  ErknmResultDecisionResponsibleEntitiesItemWithoutStore,
);
